/* eslint-disable react/prop-types */
import React, { useEffect, useState, useRef } from 'react';

import { ThumbsDown, ThumbsUp, Hourglass, Table2 } from 'lucide-react';
import { Button as CrceButton } from '@crc-evans/ui-components';
import { Calendar, Spin, Modal, Row, Col, Tooltip, Table, Select, Radio } from 'antd';
import moment from 'moment';
import { OnFetchCalenderDetails, OnFetchLogDetails } from '../../redux/PQTlogDetails/saga';
import { PROJECT_TABLE_LOADER_INDICATOR, getProjectTableLoadingConfig } from '../ProjectTableLoader';

/** Aligns with backend UpdatePqtWeldService validation for Table / Value tests */
const REJECT_CELL_COLOR = '#cf1322';

/** Same window as antd CalendarHeader YearSelect (generateCalendar) */
const PQT_CAL_YEAR_OFFSET = 10;
const PQT_CAL_YEAR_SPAN = 20;

/**
 * Light, CRC-style year/month controls + mode switch (replaces default Calendar header Selects / dark dropdown).
 */
function PqtResultsCalendarHeader({ value, type, onChange, onTypeChange }) {
  const m = value && moment.isMoment(value) ? value : moment(value);
  const year = m.year();
  const month = m.month();
  const startY = year - PQT_CAL_YEAR_OFFSET;
  const yearOptions = [];
  for (let y = startY; y < startY + PQT_CAL_YEAR_SPAN; y += 1) {
    yearOptions.push({ value: y, label: String(y) });
  }
  const monthOptions = moment.monthsShort().map((label, idx) => ({ value: idx, label }));

  /* Body avoids dark/inherited styles from calendar card and matches light dropdown CSS */
  const popupContainer = () => document.body;

  return (
    <div className="pqt-results-cal-header-custom">
      <div className="pqt-results-cal-header-selects">
        <Select
          className="pqt-results-cal-year-select"
          dropdownClassName="pqt-results-cal-select-dropdown"
          getPopupContainer={popupContainer}
          size="small"
          value={year}
          options={yearOptions}
          onChange={(y) => onChange(m.clone().year(y))}
        />
        {type === 'month' ? (
          <Select
            className="pqt-results-cal-month-select"
            dropdownClassName="pqt-results-cal-select-dropdown"
            getPopupContainer={popupContainer}
            size="small"
            value={month}
            options={monthOptions}
            onChange={(mo) => onChange(m.clone().month(mo))}
          />
        ) : null}
      </div>
      <Radio.Group
        className="pqt-results-cal-mode-switch"
        value={type}
        size="small"
        onChange={(e) => onTypeChange(e.target.value)}
      >
        <Radio.Button value="month">Month</Radio.Button>
        <Radio.Button value="year">Year</Radio.Button>
      </Radio.Group>
    </div>
  );
}

function isValidJson(str) {
  if (str == null || str === '' || typeof str !== 'string') return false;
  try {
    JSON.parse(str);
    return true;
  } catch {
    return false;
  }
}

/** Append unit to a table cell for display (row-level unit from definition, else test-level). */
function formatTableCellWithUnit(rawValue, rowUnit, fallbackUnit) {
  if (rawValue === '' || rawValue == null) return rawValue;
  const u = String(rowUnit ?? '').trim() || String(fallbackUnit ?? '').trim();
  if (!u) return String(rawValue);
  const body = String(rawValue).trim();
  if (body.toLowerCase().endsWith(u.toLowerCase())) return body;
  return `${body} ${u}`.trim();
}

function isValueInRangeWithParams(value, comparator, minValue, maxValue) {
  const n = (v) => (typeof v === 'number' && !Number.isNaN(v) ? v : NaN);
  const minV = n(minValue);
  const maxV = n(maxValue);

  switch (comparator) {
    case '<':
      return !Number.isNaN(maxV) && value < maxV;
    case '<=':
      return !Number.isNaN(maxV) && value <= maxV;
    case '>':
      return !Number.isNaN(minV) && value > minV;
    case '>=':
      return !Number.isNaN(minV) && value >= minV;
    case '=':
      return !Number.isNaN(maxV) && value === maxV;
    case 'BETWEEN':
      return !Number.isNaN(minV) && !Number.isNaN(maxV) && value >= minV && value <= maxV;
    default:
      return false;
  }
}

function validateTableRow(row, test) {
  const { values, maxValue, minValue, comparator } = row;
  // console.log('row', row);
  // console.log('test', test);

  // console.log('test.max_value', test.max_value);
  // console.log('test.min_value', test.min_value);


  const rowMaxValue = maxValue ? parseFloat(maxValue) : test.max_value;
  const rowMinValue = minValue ? parseFloat(minValue) : test.min_value;
  const rowComparator = test.comparator || comparator;
  // const rowComparator = comparator || test.comparator;

  // console.log('rowComparator', rowComparator);
  // console.log('rowMinValue', rowMinValue);
  // console.log('rowMaxValue', rowMaxValue);

  if (rowComparator === 'None') {
    return true;
  }

  if (!rowComparator) {
    return false;
  }

  for (const cell of values || []) {
    if (cell === '' || cell == null) {
      return false;
    }
    const numericValue = parseFloat(cell);
    if (Number.isNaN(numericValue)) {
      return false;
    }
    if (!isValueInRangeWithParams(numericValue, rowComparator, rowMinValue, rowMaxValue)) {
      return false;
    }
  }

  return true;
}

function validateTableTest(testValue, weldTest) {
  if (!isValidJson(testValue)) {
    return 'reject';
  }
  try {
    // console.log('testValue....', testValue);
    const tableData = JSON.parse(testValue);
    // console.log('tableData....', tableData);
    if (!tableData.rows || !Array.isArray(tableData.rows)) {
      return 'reject';
    }
    for (const row of tableData.rows) {
      if (!validateTableRow(row, weldTest)) {
        // console.log('reject....');
        return 'reject';
      }
    }
    // console.log('accept....');
    return 'accept';
  } catch {
    return 'reject';
  }
}

function validateNumericValue(testValue, test) {
  if (!isValidJson(testValue)) {
    return 'reject';
  }
  try {
    const parsedValue = JSON.parse(testValue);
    const comparator = test.comparator;
    const minValue =
      test.min_value != null && test.min_value !== '' ? parseFloat(test.min_value) : NaN;
    const maxValue =
      test.max_value != null && test.max_value !== '' ? parseFloat(test.max_value) : NaN;

    if (Array.isArray(parsedValue)) {
      if (parsedValue.includes(null)) {
        return 'reject';
      }
      for (const v of parsedValue) {
        const num = typeof v === 'number' ? v : parseFloat(v);
        if (Number.isNaN(num)) {
          return 'reject';
        }
        if (!isValueInRangeWithParams(num, comparator, minValue, maxValue)) {
          return 'reject';
        }
      }
      return 'accept';
    }

    const num = typeof parsedValue === 'number' ? parsedValue : parseFloat(parsedValue);
    if (Number.isNaN(num)) {
      return 'reject';
    }
    return isValueInRangeWithParams(num, comparator, minValue, maxValue) ? 'accept' : 'reject';
  } catch {
    return 'reject';
  }
}

/** Mirrors UpdatePqtWeldService.calculateTestStatus for grid display (Table / Value emphasis). */
function calculatePqtTestStatus(item) {
  const valueType = item.test_value_type?.value_type;
  const typeOfTest = item.type_of_test;
  const testValue = item.value;
  const mandatory = item.mandatory;
  const notApplicable = item.not_applicable;
  const comparator = item.comparator;

  if (notApplicable === 'yes') {
    return 'accept';
  }
  if (typeOfTest === 'Text') {
    return 'accept';
  }
  if (testValue === 'update_pending') {
    return 'pending';
  }

  if (typeOfTest === 'Table') {
    // console.log('validateTableTest....', validateTableTest(String(testValue ?? ''), item));
    return validateTableTest(String(testValue ?? ''), item);
  }

  // Value tests — mirror UpdatePqtWeldService.calculateTestStatus (Value branch)
  if (typeOfTest === 'Value') {
    if (testValue === 'accept') {
      return 'accept';
    }

    if (valueType === 'ACCEPT_OR_REJECT_OR_NA') {
      if (testValue === 'N/A') {
        return 'accept';
      }
      if (testValue === 'reject') {
        return 'reject';
      }
      return 'reject';
    }

    if (valueType !== 'ACCEPT_OR_REJECT_OR_NA') {
      if ((testValue === '' || testValue == null) && !mandatory) {
        return 'accept';
      }
      if (comparator === 'None') {
        return 'accept';
      }
      const hasComparator =
        comparator != null && String(comparator).trim() !== '' && comparator !== 'None';
      if (hasComparator) {
        return validateNumericValue(String(testValue ?? ''), item);
      }
    }

    return 'reject';
  }

  if (valueType === 'ACCEPT_OR_REJECT_OR_NA') {
    if (testValue === 'accept' || testValue === 'N/A') {
      return 'accept';
    }
    if (testValue === 'reject') {
      return 'reject';
    }
    return 'reject';
  }

  return 'reject';
}

const LogCalender = (props) => {
  const {
    locationData,
    dataList,
    columnList,
    showLogLoader,
    setPipeline,
    pipeline,

    setSelectedPipeline,
    selectedPipeline,
    calenderForm,
    getCalData,

    isCalenderLoading,

    onFetchLogDetails,
    setDataList,
    totalCount,
    calenderView,
    setCalenderView,
    calenderClass,
    setCalenderClass,
    selectedSOW,
    /** Optional: keep parent (e.g. PQT Results) in sync for materials/weather API date range */
    onResultsCalendarNavigate,
    /** Optional: `{ fromDate, toDate }` in `YYYY-MM-DD` — same range as log fetch (main branch) */
    onLogDateRangeChange,
    /** Parent-held range (mirror of emitted range) — used to refetch after Recalculate */
    activeLogDateRange,
    /** Increment from parent after `pqt-weld/recalculate-overall-status` to reload inspection report rows */
    inspectionLogRefreshKey = 0,
  } = props;

  const [loading, setLoading] = useState(false);
  const [logPopup, setLogPopup] = useState(false);
  const [tableDated, settableDated] = useState(false);
  // const [pipeline, setPipeline] = useState('');
  // const [selectedPipeline, setSelectedPipeline] = useState('');
  const [startDate, setStartDate] = useState(moment().startOf('year').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(moment().endOf('year').format('YYYY-MM-DD'));
  const [yearData, setYearData] = useState(moment().format('YYYY'));
  const [monthData, setMonthData] = useState(moment().format('MM'));
  const [mode, setMode] = useState(moment().format('MM'));
  const [selectedDate, setSelectedDate] = useState('');
  // const [calenderList, setCalenderList] = useState([{
  //   "date": "2025-03-14",
  //   "totalWelds": 2
  // },
  //   {
  //     "date": "2025-03-15",
  //     "totalWelds": 3
  //   },
  //   {
  //     "date": "2025-03-17",
  //     "totalWelds": 5
  //   }]);

  const [calenderList, setCalenderList] = useState([]);
  const [isResultModalVisible, setIsResultModalVisible] = useState(false);

  const emitLogDateRange = (fromDate, toDate) => {
    if (typeof onLogDateRangeChange !== 'function') return;
    if (fromDate && toDate) {
      onLogDateRangeChange({ fromDate, toDate });
    } else {
      onLogDateRangeChange(null);
    }
  };

  const [selectDataSource, setSelectDataSource] = useState([]);
  const [selectColumnSource, setSelectColumnSource] = useState([]);
  const [dataResultTable, setDataResultTable] = useState([]);

  const [transformDatas, setTransformDatas] = useState([]);
  const [columnData, setColumnData] = useState([]);

  const activeLogDateRangeRef = useRef(activeLogDateRange);
  activeLogDateRangeRef.current = activeLogDateRange;

  useEffect(() => {
    if (!inspectionLogRefreshKey) return;
    if (!selectedSOW?.id) return;
    const from = activeLogDateRangeRef.current?.fromDate;
    const to = activeLogDateRangeRef.current?.toDate;
    if (!from || !to) return;

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const logObj = {
          fromDate: from,
          toDate: to,
          sowId: selectedSOW.id,
          pageSize: 1000,
          pageIndex: 0,
        };
        const res1 = await OnFetchLogDetails(logObj);
        if (cancelled) return;
        if (res1?.dataSource[0] && res1.dataSource[0].length > 0) {
          setSelectDataSource(res1.dataSource[0]);
        } else {
          setSelectDataSource([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [inspectionLogRefreshKey, selectedSOW?.id]);

  useEffect(() => {
    if (pipeline && calenderList && calenderList.length > 0) {
      setCalenderView(true);
      setCalenderClass('view');
    } else {
      setCalenderView(true);
      setCalenderClass('view');
    }
  }, [calenderList]);

  /**Inner function for dateCellRender */
  const getListData = (value) => {
    let listData = [];
    switch (moment(value).format('YYYY-MM-DD')) {
      case moment(value).format('YYYY-MM-DD'):
        for (let i = 0; i < calenderList.length; i++) {
          if (calenderList[i].date == moment(value).format('YYYY-MM-DD')) {
            listData.push(calenderList[i]);
          }
        }
        break;

      default:
    }

    return listData || [];
  };

  /**Inner function for monthCellRender */
  const getMonthData = (value) => {
    let listData = [];
    let num = 0;
    switch (moment(value).format('YYYY-MM')) {
      case moment(value).format('YYYY-MM'):
        for (let i = 0; i < calenderList.length; i++) {
          if (moment(calenderList[i].date).format('YYYY-MM') == moment(value).format('YYYY-MM')) {
            var obj = {
              date: moment(calenderList[i].date).format('YYYY-MM'),
              totalWelds: calenderList[i].totalWelds,
            };
            listData.push(obj);
          }
        }
        if (listData.length > 0) {
          for (var i = 0; i < listData.length; i++) {
            num += listData[i].totalWelds;
          }
        }

        break;

      default:
    }
    return num;
  };

  /** Function to render Monthly weld count (For each month) */
  const weldTier = (n) => (n <= 100 ? 'low' : n <= 200 ? 'mid' : 'high');

  const monthCellRender = (value) => {
    const num = getMonthData(value);
    return num ? (
      <div className={`notes-month pqt-cal-weld-chip pqt-cal-weld-chip--month pqt-cal-weld-chip--${weldTier(num)}`}>
        <span className="pqt-cal-weld-chip__label">Welds</span>
        <span className="pqt-cal-weld-chip__count">{num}</span>
      </div>
    ) : null;
  };

  /**Function to render dated welds count */
  const dateCellRender = (value) => {
    const listData = getListData(value);
    return (
      <>
        {listData.map((item) => (
          <div
            onClick={() => handleLogChange(item)}
            className={`events pqt-cal-weld-chip pqt-cal-weld-chip--day pqt-cal-weld-chip--${weldTier(item.totalWelds)}`}
            key={`${item.date}-${item.totalWelds}`}
            role="presentation"
          >
            <span className="pqt-cal-weld-chip__label">Welds</span>
            <span className="pqt-cal-weld-chip__count">{item.totalWelds}</span>
          </div>
        ))}
      </>
    );
  };

  /** On selection of sow (pipeline) */
  const handlePipelineChange = (value) => {
    setPipeline(value);
    if (locationData.length > 0) {
      const locData = locationData.find((u) => u.pipelineSection === value);
      setSelectedPipeline(locData);
    }
  };

  const handleCancel = () => {
    setLogPopup(false);
    setDataList([]);
  };

  const handleCalederChange = (value, mode) => {
    // console.log('handleCalederChange', value.format('YYYY-MM-DD'), mode);
  };

  const onPanelChange = async (value, mode) => {
    //console.log("Selected:", value.format('YYYY-MM-DD'), "Mode:", mode);
    setSelectedDate(value.format('YYYY-MM-DD'));
    setMode(mode);
    setLoading(true);

    // Update the selected month & year
    const selectedYear = value.year();
    const selectedMonth = value.format('MM'); // Ensures two-digit format

    if (typeof onResultsCalendarNavigate === 'function') {
      onResultsCalendarNavigate({
        day: value.format('YYYY-MM-DD'),
        mode,
        year: selectedYear,
        month: selectedMonth,
      });
    }

    // Create request payload for API call
    const obj = {
      sowId: selectedSOW.id, // Ensure `selectedSOW` is defined in state
      month: selectedMonth, // Selected month in "MM" format
      year: selectedYear, // Selected year as number
    };


    try {
      const res = await OnFetchCalenderDetails(obj);
      //console.log("Fetched Data:", res);

      if (res && res.length > 0) {
        await setCalenderList(res); // Update state with fetched data
        setMonthData(selectedMonth);
        setYearData(selectedYear);
        if (mode === 'year') {
          const mothsta = moment(`${selectedYear}-${selectedMonth}-01`).startOf('month'); // First day of selected month
          const end = moment(`${selectedYear}-${selectedMonth}-31`).endOf('month'); // Last day of selected month

          const logObj = {
            fromDate: mothsta.format('YYYY-MM-DD'),
            toDate: end.format('YYYY-MM-DD'),
            sowId: selectedSOW.id,
            pageSize: 1000,
            pageIndex: 0,
          };


          if (!logObj.fromDate || !logObj.toDate || logObj.toDate === 'Invalid date') {
            setLoading(false);
            return;
          }

          const res1 = await OnFetchLogDetails(logObj);
          if (res1?.dataSource[0] && res1?.dataSource[0].length > 0)
            setSelectDataSource(res1?.dataSource[0]);
          else setSelectDataSource([]);
          emitLogDateRange(logObj.fromDate, logObj.toDate);
        } else {
          const dayStr = value.format('YYYY-MM-DD');
          const logObj = {
            fromDate: dayStr,
            toDate: dayStr,
            sowId: selectedSOW.id,
            pageSize: 1000,
            pageIndex: 0,
          };

          if (!logObj.fromDate || !logObj.toDate) {
            setLoading(false);
            return;
          }

          // console.log("Fetching log details for:", logObj);
          const res1 = await OnFetchLogDetails(logObj);
          // console.log("Log Details Response:", res1);

          // console.log(" Results Log Details Response:", res1?.dataSource[0]);
          if (res1?.dataSource[0] && res1?.dataSource[0].length > 0)
            setSelectDataSource(res1?.dataSource[0]);
          else setSelectDataSource([]);
          emitLogDateRange(dayStr, dayStr);
        }
      } else {
        setCalenderList([]); // Handle empty response
        setSelectDataSource([]);
        emitLogDateRange(null, null);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching calendar details:', error);
      setLoading(false);
    }
  };
  const handleLogChange = async (item) => {
    if (item) {
      //const projectId = get(selectedProject, 'id', undefined);

      setLoading(true);

      if (mode === 'year') {
        if (typeof onResultsCalendarNavigate === 'function') {
          onResultsCalendarNavigate({
            day: null,
            mode: 'year',
            year: Number(yearData),
            month: monthData,
          });
        }
        const y = Number(yearData);
        const m = String(monthData).padStart(2, '0');
        const mothsta = moment(`${y}-${m}-01`, 'YYYY-MM-DD').startOf('month');
        const end = moment(`${y}-${m}-01`, 'YYYY-MM-DD').endOf('month');

        const logObj = {
          fromDate: mothsta.format('YYYY-MM-DD'),
          toDate: end.format('YYYY-MM-DD'),
          sowId: selectedSOW.id,
          pageSize: 1000,
          pageIndex: 0,
        };

        const res = await OnFetchLogDetails(logObj);
        if (res?.dataSource[0] && res?.dataSource[0].length > 0) {
          setSelectDataSource(res?.dataSource[0]);
        } else setSelectDataSource([]);
        emitLogDateRange(logObj.fromDate, logObj.toDate);
      } else {
        var obj = {
          fromDate: item.date,
          toDate: item.date,
          sowId: selectedSOW.id,
          pageSize: 1000,
          pageIndex: 0,
        };
        setSelectedDate(item.date);
        if (typeof onResultsCalendarNavigate === 'function') {
          const d = moment(item.date);
          onResultsCalendarNavigate({
            day: item.date,
            mode: 'month',
            year: d.year(),
            month: d.format('MM'),
          });
        }
        // console.log("Fetching log details for:", obj);

        const res = await OnFetchLogDetails(obj); // Fetch data based on the selected date range
        if (res && res.dataSource && res.dataSource[0].length > 0) {
          setSelectDataSource(res?.dataSource[0]);
        } else setSelectDataSource([]);
        emitLogDateRange(item.date, item.date);
      }
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!selectedSOW || !selectedSOW.id) {
        emitLogDateRange(null, null);
        return;
      }

      setLoading(true); // Start loading

      const obj = {
        sowId: selectedSOW.id,
        month: monthData,
        year: yearData,
      };

      try {
        const res = await OnFetchCalenderDetails(obj);

        if (res && res.length > 0) {
          setCalenderList(res);
          const logObj = {
            fromDate: moment().format('YYYY-MM-DD'),
            toDate: moment().format('YYYY-MM-DD'),
            sowId: selectedSOW.id,
            pageSize: 1000,
            pageIndex: 0,
          };

          const res1 = await OnFetchLogDetails(logObj);
          if (res1?.dataSource[0] && res1?.dataSource[0].length > 0) {
            setSelectDataSource(res1?.dataSource[0]);
          } else {
            setSelectDataSource([]);
          }
          emitLogDateRange(logObj.fromDate, logObj.toDate);
        } else {
          setCalenderList([]);
          setSelectDataSource([]);
          emitLogDateRange(null, null);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false); // Stop loading
      }
    };

    fetchData();
  }, [selectedSOW]);

  const transformData = () => {
    let transformedRows = {};

    // Extract unique weld IDs
    const weldIds = [...new Set(selectDataSource.map((item) => item.weld_id))];

    const getUniString = (item) => {
      if (item.value == null || String(item.value).trim() === '') return '-';

      const raw = String(item.value).trim();
      const unit = item.test_value_type?.display_str || '';

      const parseAsJsonArray = (str) => {
        const t = str.trim();
        if (!(t.startsWith('[') && t.endsWith(']'))) return null;
        try {
          const p = JSON.parse(t);
          return Array.isArray(p) ? p : null;
        } catch {
          try {
            const p = JSON.parse(t.replace(/\\"/g, '"'));
            return Array.isArray(p) ? p : null;
          } catch {
            return null;
          }
        }
      };

      const cleanSegment = (s) =>
        String(s)
          .trim()
          .replace(/\\"/g, '"')
          .replace(/^["']+|["']+$/g, '')
          .replace(/^\[|\]$/g, '')
          .trim();

      let parts;
      const parsed = parseAsJsonArray(raw);
      if (parsed) {
        parts = parsed.map((v) => (v == null ? '' : String(v).trim())).filter((p) => p !== '');
      } else {
        parts = raw
          .replace(/^\[|\]$/g, '')
          .split(',')
          .map((s) => cleanSegment(s))
          .filter((p) => p !== '');
      }

      if (parts.length === 0) return '-';

      const body = parts.join(' / ').replace(/\[|\]/g, '').trim();
      return unit ? `${body} ${unit}`.trim() : body;
    };

    // Transform data while merging duplicate process descriptions
    selectDataSource
      .filter((item) => item.process_description !== 'PROCESS DESCRIPTION')
      .forEach((item) => {
        const processKey = item.process_description;

        // If process already exists, update it; otherwise, create a new entry
        if (!transformedRows[processKey]) {
          transformedRows[processKey] = {
            key: processKey,
            process_description: processKey,
          };

          // Initialize all weld_id columns to "-"
          weldIds.forEach((weldId) => {
            transformedRows[processKey][weldId] = '-';
          });
        }

        // Update the correct weld_id column (red when client-side validation matches backend rules)
        const rejectStyle = { color: REJECT_CELL_COLOR };
        if (item.type_of_test === 'Table') {
          const status = calculatePqtTestStatus(item);
          const isReject = status === 'reject';
          transformedRows[processKey][item.weld_id] =
            item.value && item.value.trim() !== '' ? (
            <span style={isReject ? rejectStyle : undefined}>
              <Table2
                role="button"
                tabIndex={0}
                className="pqt-results-table-open-icon"
                size={20}
                strokeWidth={2}
                aria-label="Open table"
                onClick={() => {
                  setDataResultTable(item);
                  setIsResultModalVisible(true);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setDataResultTable(item);
                    setIsResultModalVisible(true);
                  }
                }}
              />
            </span>
            ) : (
              '-'
            );
        } else if (item.type_of_test === 'Value') {
          const status = calculatePqtTestStatus(item);
          const isReject = status === 'reject';
          transformedRows[processKey][item.weld_id] = (
            <span style={isReject ? rejectStyle : undefined}>{getUniString(item)}</span>
          );
        } else {
          transformedRows[processKey][item.weld_id] = item.value || '-';
        }
      });

    // Convert object back to array
    let finalRows = Object.values(transformedRows);

    // Append last rows (repair_status & overall_status)
    ['repair_status', 'overall_status'].forEach((param) => {
      let row = { key: param, process_description: param.replace(/_/g, ' ').toUpperCase() };

      weldIds.forEach((weldId) => {
        const record = selectDataSource.find((item) => item.weld_id === weldId);
        row[weldId] = record ? record[param] : '-';
      });

      finalRows.push(row);
    });

    return finalRows;
  };

  const getColumnData = () => {
    // Extract unique weld IDs
    const weldIds = [...new Set(selectDataSource.map((item) => item.weld_id))];

    // Define dynamic columns
    const columns = [
      {
        title: 'Process Description',
        dataIndex: 'process_description',
        key: 'process_description',
        width: 200,
        fixed: true,
      },
      ...weldIds.map((weldId) => ({
        title: weldId,
        dataIndex: weldId,
        key: weldId,
        align: 'center',
        width: 120,
        render: (text) =>
          text === 'accept' ? (
            <Tooltip title="Accepted">
              <ThumbsUp
                className="pqt-results-status-icon pqt-results-status-icon--accept"
                size={20}
                strokeWidth={2}
                aria-label="Accepted"
              />
            </Tooltip>
          ) : text === 'reject' ? (
            <Tooltip title="Rejected">
              <ThumbsDown
                className="pqt-results-status-icon pqt-results-status-icon--reject"
                size={20}
                strokeWidth={2}
                aria-label="Rejected"
              />
            </Tooltip>
          ) : text === 'pending' ? (
            <Tooltip title="Pending">
              <Hourglass
                className="pqt-results-status-icon pqt-results-status-icon--pending"
                size={20}
                strokeWidth={2}
                aria-hidden
              />
            </Tooltip>
          ) : (
            text
          ),
      })),
    ];

    return columns;
  };

  const getTableColumns = (item) => {
    if (!item || !item.value) return []; // Handle empty item case

    const unit = item.test_value_type?.display_str || ''; // Extract unit
    let columns = [
      {
        title: `${unit}`, // First column is the unit
        dataIndex: 'name',
        key: 'name',
      },
    ];

    try {
      const parsedData = JSON.parse(item.value); // Parse JSON safely

      if (!parsedData.columns || !Array.isArray(parsedData.columns)) return columns; // Ensure columns exist

      parsedData.columns.forEach((col, index) => {
        columns.push({
          title: col, // Column title from JSON
          dataIndex: `col_${index}`, // Unique data key
          key: `col_${index}`,
          align: 'center',
        });
      });
    } catch (error) {
      console.error('Error parsing JSON in getTableColumns:', error);
    }

    return columns;
  };

  const getTableData = (item) => {
    if (!item || !item.value) return [];

    try {
      const parsedData = JSON.parse(item.value); // Safely parse JSON
      if (!parsedData.rows || !Array.isArray(parsedData.rows)) return []; // Ensure rows exist

      const fallbackUnit = item.test_value_type?.display_str || '';

      return parsedData.rows.map((row, rowIndex) => {
        let rowData = { key: rowIndex, name: row.name };

        row.values.forEach((value, index) => {
          rowData[`col_${index}`] = formatTableCellWithUnit(value, row.unit, fallbackUnit);
        });

        return rowData;
      });
    } catch (error) {
      console.error('Error parsing JSON in getTableData:', error);
      return [];
    }
  };

  return (
    <div>
      <Row gutter={[16, 16]} className="pqt-results-log-row">
        {/* Calendar Section */}
        <Col xs={24} lg={8}>
          <div className="pqt-results-cal-wrap">
            <Calendar
              headerRender={(p) => <PqtResultsCalendarHeader {...p} />}
              dateCellRender={dateCellRender}
              monthCellRender={monthCellRender}
              onSelect={handleCalederChange}
              onPanelChange={onPanelChange}
            />
          </div>
        </Col>

        {/* Table Section (Displays when a date is selected) */}
        <Col xs={24} lg={16} style={{ overflowX: 'auto' }}>
          {loading ? (
            <Spin size="large" indicator={PROJECT_TABLE_LOADER_INDICATOR} />
          ) : selectDataSource && selectDataSource.length > 0 ? (
            <div className="addPQTDetails">
              <Table
                columns={getColumnData()}
                dataSource={transformData()}
                pagination={{
                  pageSize: 15,
                  hideOnSinglePage: true,
                  showSizeChanger: false,
                  className: 'heat-profile-pagination',
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                }}
                width="100%"
                scroll={{ x: 'max-content' }}
                className="pqt-results-table-shell"
              />
            </div>
          ) : (
            <Table
              loading={getProjectTableLoadingConfig(loading)}
              className="pqt-results-table-shell"
            />
          )}
        </Col>
      </Row>

      <Modal
        width="50vw"
        wrapClassName="pqt-results-table-modal-wrap"
        className="pqt-results-table-modal"
        title="Table Results"
        centered
        visible={isResultModalVisible}
        footer={[
          <CrceButton
            key="cancel"
            label="Close"
            variant="tertiary"
            size="medium"
            onClick={() => {
              setIsResultModalVisible(false);
            }}
          />,
        ]}
      >
        <div>
          <Table
            columns={getTableColumns(dataResultTable)}
            dataSource={getTableData(dataResultTable)}
            width={'100%'}
            pagination={{
              pageSize: 12,
              hideOnSinglePage: true,
              className: 'heat-profile-pagination',
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
            }}
            scroll={{ x: 'max-content' }}
            className="pqt-results-modal-inner-table"
          />
        </div>
      </Modal>
    </div>
  );
};

export default LogCalender;
